import { useCallback, useEffect, useMemo, useState } from 'react';
import { zinzinoCockpitAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Target, ChevronLeft, ChevronRight, Save, Loader2, Trash2,
  CalendarClock, FlaskConical, UserCheck, Users, TrendingUp,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* Zinzino Partner-Cockpit — misst die Wochenarbeit von Philip & Elvan gegen
   die Planwerte aus dem Geschäftsmodell-Dokument ("Zinzino und Personal
   Training", 16.08.2026). Interne Steuerungsseite: Zahlen sind Arbeitsziele
   und Brutto-Planwerte, keine Einkommensgarantie.

   Die Planwerte kommen vom Server (/api/zinzino-cockpit) — hier ist nichts
   hartkodiert außer Beschriftungen. */

const PERSONEN = [
  { key: 'philip', label: 'Philip' },
  { key: 'elvan', label: 'Elvan' },
];

const METRIKEN = [
  { key: 'ansprachen', label: 'Gezielte Ansprachen' },
  { key: 'gespraeche', label: 'Qualifizierte Gespräche' },
  { key: 'beratungen', label: 'Durchgeführte Beratungen' },
  { key: 'neue_kunden', label: 'Neue zahlende Kunden' },
  { key: 'nachfaesse', label: 'Nachfassgespräche' },
];

const LEER = {
  ansprachen: '', gespraeche: '', beratungen: '',
  neue_kunden: '', nachfaesse: '', aktive_kunden: '', aktive_partner: '',
};

/** Montag (ISO) der Woche, die `offset` Wochen vor der aktuellen liegt. */
function montag(offset = 0) {
  const d = new Date();
  const tag = (d.getDay() + 6) % 7; // Mo=0 … So=6
  d.setDate(d.getDate() - tag - offset * 7);
  return d.toISOString().slice(0, 10);
}

const fmtWoche = (iso) => {
  const [y, m, t] = iso.split('-');
  const start = `${t}.${m}.`;
  const e = new Date(`${iso}T00:00:00`);
  e.setDate(e.getDate() + 6);
  return `${start} – ${String(e.getDate()).padStart(2, '0')}.${String(e.getMonth() + 1).padStart(2, '0')}.${y}`;
};

const AMPEL_FARBEN = {
  gruen: 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/40',
  gelb: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40',
  rot: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/40',
  grau: 'bg-muted text-muted-foreground border-border',
};

function Balken({ ist, soll, max }) {
  const ziel = max || soll;
  const p = Math.min(100, ziel ? (ist / ziel) * 100 : 0);
  const erreicht = ist >= soll;
  return (
    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
      <div className={`h-full rounded-full transition-all ${erreicht ? 'bg-green-500' : 'bg-primary'}`}
           style={{ width: `${p}%` }} />
    </div>
  );
}

function PersonKarte({ person, plan, eintrag, onGespeichert }) {
  const [werte, setWerte] = useState(LEER);
  const [sende, setSende] = useState(false);
  const eintragWoche = person.wocheStart;

  useEffect(() => {
    if (eintrag) {
      const w = {};
      Object.keys(LEER).forEach((k) => { w[k] = eintrag[k] ?? ''; });
      setWerte(w);
    } else {
      setWerte(LEER);
    }
  }, [eintrag]);

  const setzen = (k) => (e) => {
    const v = e.target.value;
    if (v === '' || /^\d{0,5}$/.test(v)) setWerte((w) => ({ ...w, [k]: v }));
  };

  const speichern = async () => {
    setSende(true);
    try {
      const zahlen = {};
      Object.keys(LEER).forEach((k) => { zahlen[k] = Number(werte[k] || 0); });
      const r = await zinzinoCockpitAPI.wocheSchreiben({
        person: person.key, woche_start: eintragWoche, ...zahlen,
      });
      toast.success(`${person.label} gespeichert${r.data.quote_prozent != null ? ` — Quote ${r.data.quote_prozent} %` : ''}`);
      onGespeichert();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSende(false);
  };

  const soll = plan?.woche_pro_person || {};

  return (
    <Card data-testid={`cockpit-${person.key}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>{person.label}</span>
          {eintrag?.quote_prozent != null && (
            <Badge variant="outline" className="font-mono">
              Quote {eintrag.quote_prozent} %
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {METRIKEN.map((m) => {
          const zielMax = m.key === 'neue_kunden' ? soll.neue_kunden_max : undefined;
          const zielText = m.key === 'neue_kunden'
            ? `${soll.neue_kunden}–${soll.neue_kunden_max}` : soll[m.key];
          return (
            <div key={m.key}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="text-xs text-muted-foreground" htmlFor={`${person.key}-${m.key}`}>
                  {m.label} <span className="opacity-60">/ Ziel {zielText}</span>
                </label>
                <input
                  id={`${person.key}-${m.key}`}
                  inputMode="numeric"
                  value={werte[m.key]}
                  onChange={setzen(m.key)}
                  placeholder="0"
                  className="w-16 text-right text-sm rounded-md border border-border bg-background px-2 py-1"
                />
              </div>
              <Balken ist={Number(werte[m.key] || 0)} soll={soll[m.key] || 0} max={zielMax} />
            </div>
          );
        })}

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
          <div>
            <label className="text-xs text-muted-foreground" htmlFor={`${person.key}-aktive_kunden`}>
              Aktive Kunden (Stand)
            </label>
            <input id={`${person.key}-aktive_kunden`} inputMode="numeric"
                   value={werte.aktive_kunden} onChange={setzen('aktive_kunden')} placeholder="0"
                   className="mt-1 w-full text-sm rounded-md border border-border bg-background px-2 py-1" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground" htmlFor={`${person.key}-aktive_partner`}>
              Aktive Partner (Stand)
            </label>
            <input id={`${person.key}-aktive_partner`} inputMode="numeric"
                   value={werte.aktive_partner} onChange={setzen('aktive_partner')} placeholder="0"
                   className="mt-1 w-full text-sm rounded-md border border-border bg-background px-2 py-1" />
          </div>
        </div>

        <Button size="sm" className="w-full gap-1.5" onClick={speichern} disabled={sende}
                data-testid={`cockpit-speichern-${person.key}`}>
          {sende ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Woche speichern
        </Button>
      </CardContent>
    </Card>
  );
}

function Meilensteine({ plan, aktiveKunden, personLabel }) {
  const steine = plan?.meilensteine || [];
  const maxK = steine.length ? steine[steine.length - 1].kunden : 100;
  const naechster = steine.find((s) => aktiveKunden < s.kunden);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium">{personLabel}: {aktiveKunden} aktive Kunden</p>
        {naechster && (
          <p className="text-xs text-muted-foreground">
            noch {naechster.kunden - aktiveKunden} bis {naechster.titel}
          </p>
        )}
      </div>
      <div className="relative h-2 rounded-full bg-muted">
        <div className="absolute inset-y-0 left-0 rounded-full bg-primary"
             style={{ width: `${Math.min(100, (aktiveKunden / maxK) * 100)}%` }} />
        {steine.map((s) => (
          <div key={s.titel}
               className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border ${
                 aktiveKunden >= s.kunden ? 'bg-green-500 border-green-600' : 'bg-background border-border'
               }`}
               style={{ left: `calc(${(s.kunden / maxK) * 100}% - 4px)` }}
               title={`${s.titel} (${s.kunden} Kunden): ${s.vorteil}`} />
        ))}
      </div>
      <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
        {steine.map((s) => <span key={s.titel}>{s.titel} {s.kunden}</span>)}
      </div>
    </div>
  );
}

export default function ZinzinoCockpitPage() {
  const [daten, setDaten] = useState(null);
  const [offset, setOffset] = useState(0);   // 0 = aktuelle Woche

  const laden = useCallback(async () => {
    try {
      const r = await zinzinoCockpitAPI.laden(16);
      setDaten(r.data);
    } catch { toast.error('Cockpit konnte nicht geladen werden'); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const wocheStart = montag(offset);
  const wochen = useMemo(() => daten?.wochen || [], [daten]);
  const plan = daten?.plan;
  const crm = daten?.crm;
  const ampel = daten?.ampel;

  const eintraege = useMemo(() => {
    const map = {};
    wochen.forEach((w) => { map[`${w.person}:${w.woche_start}`] = w; });
    return map;
  }, [wochen]);

  const wocheSumme = (feld) => PERSONEN.reduce(
    (s, p) => s + (eintraege[`${p.key}:${wocheStart}`]?.[feld] || 0), 0);

  const letzteStaende = useMemo(() => {
    // aktueller Stand aktive Kunden/Partner = jüngster Eintrag je Person
    const st = {};
    PERSONEN.forEach((p) => {
      const e = wochen.filter((w) => w.person === p.key)
        .sort((a, b) => b.woche_start.localeCompare(a.woche_start))[0];
      st[p.key] = e || null;
    });
    return st;
  }, [wochen]);

  const historie = useMemo(() => {
    const proWoche = {};
    wochen.forEach((w) => {
      proWoche[w.woche_start] = proWoche[w.woche_start] || { woche_start: w.woche_start };
      proWoche[w.woche_start][w.person] = w;
    });
    return Object.values(proWoche).sort((a, b) => b.woche_start.localeCompare(a.woche_start));
  }, [wochen]);

  const loeschen = async (person, ws) => {
    try {
      await zinzinoCockpitAPI.wocheLoeschen(person, ws);
      toast.success('Eintrag gelöscht');
      laden();
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="zinzino-cockpit">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" /> Zinzino Partner-Cockpit
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Wochenzahlen für Philip &amp; Elvan gegen die Planwerte aus dem
            Geschäftsmodell-Dokument. Alle Zahlen sind Arbeitsziele und
            Brutto-Planwerte — keine Einkommensgarantie.
          </p>
        </div>
      </div>

      {/* Ampel */}
      {ampel && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${AMPEL_FARBEN[ampel.stufe] || AMPEL_FARBEN.grau}`}
             data-testid="cockpit-ampel">
          <span className="font-semibold mr-1">
            {{ gruen: 'Skalieren', gelb: 'Auf Kurs halten', rot: 'Prozess verbessern', grau: 'Startklar' }[ampel.stufe]}:
          </span>
          {ampel.text}
        </div>
      )}

      {/* CRM-Ist (automatisch) */}
      {crm && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: CalendarClock, label: 'Offene BalanceTest-Termine', wert: crm.termine_gebucht },
            { icon: UserCheck, label: 'Beratungen durchgeführt (CRM)', wert: crm.beratungen_durchgefuehrt },
            { icon: FlaskConical, label: 'Tests übergeben', wert: crm.tests_uebergeben },
            { icon: Users, label: 'Kunden (aus Terminen)', wert: crm.kunden },
          ].map((k) => (
            <Card key={k.label}>
              <CardContent className="pt-4 pb-3">
                <k.icon className="h-4 w-4 text-muted-foreground mb-1.5" />
                <p className="text-2xl font-bold leading-none">{k.wert}</p>
                <p className="text-xs text-muted-foreground mt-1">{k.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Wochenwahl */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setOffset((o) => o + 1)}
                  data-testid="cockpit-woche-zurueck">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium min-w-[190px] text-center">
            Woche {fmtWoche(wocheStart)}
            {offset === 0 && <Badge variant="secondary" className="ml-2">aktuell</Badge>}
          </div>
          <Button size="sm" variant="outline" onClick={() => setOffset((o) => Math.max(0, o - 1))}
                  disabled={offset === 0} data-testid="cockpit-woche-vor">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {plan && (
          <p className="text-xs text-muted-foreground">
            Wichtigste Zahl: zusammen mind. {plan.woche_zusammen.neue_kunden_min} neue
            Kunden/Woche — diese Woche: <b>{wocheSumme('neue_kunden')}</b>
          </p>
        )}
      </div>

      {/* Erfassung pro Person */}
      <div className="grid md:grid-cols-2 gap-4">
        {PERSONEN.map((p) => (
          <PersonKarte key={`${p.key}:${wocheStart}`}
                       person={{ ...p, wocheStart }}
                       plan={plan}
                       eintrag={eintraege[`${p.key}:${wocheStart}`]}
                       onGespeichert={laden} />
        ))}
      </div>

      {/* Meilensteine */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Kundentitel-Meilensteine
            <span className="text-xs font-normal text-muted-foreground">
              (90-Tage-Ziel: A-Team, 25 aktive Kunden pro Person)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {PERSONEN.map((p) => (
            <Meilensteine key={p.key} plan={plan} personLabel={p.label}
                          aktiveKunden={letzteStaende[p.key]?.aktive_kunden || 0} />
          ))}
        </CardContent>
      </Card>

      {/* Historie */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Wochenhistorie</CardTitle>
        </CardHeader>
        <CardContent>
          {historie.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Noch keine Wochen erfasst. Trag oben die erste Woche ein — ab dann
              misst das Cockpit automatisch gegen den Plan.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3">Woche</th>
                    <th className="py-2 pr-3">Person</th>
                    <th className="py-2 pr-3 text-right">Anspr.</th>
                    <th className="py-2 pr-3 text-right">Gespr.</th>
                    <th className="py-2 pr-3 text-right">Berat.</th>
                    <th className="py-2 pr-3 text-right">Neu</th>
                    <th className="py-2 pr-3 text-right">Nachf.</th>
                    <th className="py-2 pr-3 text-right">Quote</th>
                    <th className="py-2 pr-3 text-right">Aktive</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {historie.map((h) => PERSONEN.filter((p) => h[p.key]).map((p, i) => {
                    const w = h[p.key];
                    const zielNeu = plan?.woche_pro_person?.neue_kunden || 3;
                    return (
                      <tr key={`${h.woche_start}-${p.key}`} className="border-b border-border/50">
                        <td className="py-1.5 pr-3 whitespace-nowrap">{i === 0 ? fmtWoche(h.woche_start) : ''}</td>
                        <td className="py-1.5 pr-3">{p.label}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">{w.ansprachen}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">{w.gespraeche}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">{w.beratungen}</td>
                        <td className={`py-1.5 pr-3 text-right font-mono font-semibold ${
                          w.neue_kunden >= zielNeu ? 'text-green-600 dark:text-green-400' : ''}`}>
                          {w.neue_kunden}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono">{w.nachfaesse}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">
                          {w.quote_prozent != null ? `${w.quote_prozent} %` : '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono">{w.aktive_kunden}</td>
                        <td className="py-1.5 text-right">
                          <button className="text-muted-foreground hover:text-destructive"
                                  title="Eintrag löschen"
                                  onClick={() => loeschen(p.key, h.woche_start)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 90-Tage-Plan */}
      {plan && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Der 90-Tage-Plan</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-3">
            {plan.phasen_90_tage.map((ph) => (
              <div key={ph.monat} className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Monat {ph.monat}</p>
                <p className="text-lg font-bold">{ph.aktive_kunden_pro_person} Kunden</p>
                <p className="text-xs text-muted-foreground">pro Person · {ph.stufe}</p>
              </div>
            ))}
            <p className="md:col-span-3 text-xs text-muted-foreground">
              Quelle: {plan.quelle}. {plan.hinweis} Entscheidung nach 90 Tagen:
              Skalieren ab {plan.quote_min_prozent} % Abschlussquote und{' '}
              {plan.woche_zusammen.neue_kunden_min} neuen Kunden pro Woche zusammen —
              sonst Prozess verbessern (Zielgruppe, Erklärung, Nachfassen).
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
